/*
 * PLAYXWORLD SUPPORT PAGE — MONOCHROME BRUTALISM
 * FAQ accordion + Contact info in editorial layout
 */
import Layout from "@/components/Layout";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, MessageCircle, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";

function FAQItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-3 border-black">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-black/5 transition-colors"
      >
        <div className="flex items-start gap-4">
          <span className="font-heading font-bold text-2xl text-black/20 shrink-0">
            {String(index + 1).padStart(2, "0")}
          </span>
          <h3 className="font-heading font-bold text-base md:text-lg">{q}</h3>
        </div>
        <ChevronDown
          size={20}
          className={`shrink-0 ml-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pl-14 text-black/70 text-sm leading-relaxed border-t-2 border-black/10 pt-4">
              {a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Support() {
  const { t } = useTranslation();

  const faqs = [
    { q: t("support.faqs.deliveryTime.q"), a: t("support.faqs.deliveryTime.a") },
    { q: t("support.faqs.paymentMethods.q"), a: t("support.faqs.paymentMethods.a") },
    { q: t("support.faqs.topupNotReceived.q"), a: t("support.faqs.topupNotReceived.a") },
    { q: t("support.faqs.refund.q"), a: t("support.faqs.refund.a") },
    { q: t("support.faqs.membership.q"), a: t("support.faqs.membership.a") },
    { q: t("support.faqs.promotions.q"), a: t("support.faqs.promotions.a") },
  ];

  return (
    <Layout>
      {/* Page Header */}
      <section className="bg-white text-black border-b-3 border-black">
        <div className="container py-12 md:py-16">
          <span className="font-mono text-xs tracking-widest text-black/50">"HELP CENTER"</span>
          <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight mt-2">
            {t("support.title")}
          </h1>
          <p className="text-black/60 mt-3 max-w-xl">
            {t("support.subtitle")}
          </p>
        </div>
      </section>

      <section className="bg-white text-black py-12 md:py-16">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* FAQ */}
            <div className="lg:col-span-2">
              <div className="mb-8 border-b-3 border-black pb-3">
                <span className="font-mono text-xs tracking-widest text-black/50">"FAQ"</span>
                <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight mt-1">
                  {t("support.faqTitle")}
                </h2>
              </div>
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <FAQItem key={i} q={faq.q} a={faq.a} index={i} />
                ))}
              </div>
            </div>

            {/* Contact */}
            <div>
              <div className="mb-8 border-b-3 border-black pb-3">
                <span className="font-mono text-xs tracking-widest text-black/50">"CONTACT"</span>
                <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight mt-1">
                  {t("support.contactTitle")}
                </h2>
              </div>

              <div className="space-y-4">
                {[
                  {
                    icon: MessageCircle,
                    title: t("support.lineTitle"),
                    detail: t("support.lineHandle"),
                    sub: t("support.lineSub"),
                  },
                  {
                    icon: Mail,
                    title: t("support.emailTitle"),
                    detail: t("support.emailAddress"),
                    sub: t("support.emailSub"),
                  },
                ].map((contact) => (
                  <div
                    key={contact.title}
                    className="card-brutal bg-white p-5"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 border-3 border-black flex items-center justify-center shrink-0">
                        <contact.icon size={20} />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-sm tracking-wider">
                          {contact.title}
                        </h3>
                        <p className="font-mono text-lg font-semibold mt-1">
                          {contact.detail}
                        </p>
                        <p className="text-black/50 text-xs font-mono mt-1">
                          {contact.sub}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Operating Hours */}
              <div className="mt-6 card-brutal bg-black text-white p-5">
                <h3 className="font-heading font-bold text-sm tracking-wider mb-3">
                  "OPERATING HOURS"
                </h3>
                <div className="space-y-2 font-mono text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/60">{t("support.hoursTopup")}</span>
                    <span className="font-semibold">24/7</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">{t("support.hoursSupport")}</span>
                    <span className="font-semibold">24/7</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">{t("support.hoursLine")}</span>
                    <span className="font-semibold">{t("support.hoursLineValue")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
